/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @author Vladislav Urbánek <urbanek.vlada@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';

// TODO #295 Nejasné rozdělení odpovědnosti mezi ovladačem a PdfParent:
// - Oba přistupují k elementů. Kdo má co dělat?
// - Ovladač zatím vždy řešil pozicování, tak by k němu měl mít přístup.
// - PdfParent potřebuje iframe pro komunikaci, takže zde je také přístup požadovaný.
/**
 * Ovladač pro zobrazování PDF souborů.
 * @implements {ViewerController}
 */
class PdfViewerController {
  /**
   * Vytvoří instanci.
   * @param {ViewerLifeCycleFactory} viewerLifeCycleFactory
   */
  constructor(viewerLifeCycleFactory) {
    /**
     * Správce událostí
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.onDurationchange = this.onDurationchange.bind(this);
    this.onElementError = this.onElementError.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.onParentError = this.onParentError.bind(this);
    this.parent = null;
    this.scheduleInfo = null;
    this.viewerLifeCycle = viewerLifeCycleFactory.create();
    this.viewerSource = null;
  }

  /**
   * Naváže naslouchání událostem z elementu.
   * @param {HTMLIFrameElement} el Element, kterému se naslouchá.
   * @private
   * @returns {undefined}
   */
  bindElement(el) {
    el.addEventListener('error', this.onElementError);
  }

  /**
   * Naváže naslouchání událostem z PDF přehrávače.
   * @param {PdfParent} parent
   * @private
   * @returns {undefined}
   */
  bindParent(parent) {
    parent.on('durationchange', this.onDurationchange);
    parent.on('ended', this.onEnded);
    parent.on('error', this.onParentError);
  }

  /** @inheritdoc */
  destroy() {
    const { parent, viewerSource } = this;
    if (!isNil(parent)) {
      this.unbindParent(parent);
    }
    if (!isNil(viewerSource)) {
      const el = viewerSource.getEl();
      this.unbindElement(el);
    }
  }

  /** @inheritdoc */
  enter() {
    return this.viewerLifeCycle.enter(() => {
      // Zobrazení obalujícího elementu.
      const frame = this.viewerSource.getFrame();
      frame.enter();
      // Spuštění přehrávání.
      return this.parent.play();
    });
  }

  /** @inheritdoc */
  leave() {
    return this.viewerLifeCycle.leave(() => {
      // Skrytí obalujícího elementu.
      const frame = this.viewerSource.getFrame();
      frame.leave();
      // Zastavení přehrávání.
      return this.parent.pause();
    });
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na změnu doby přehrávání.
   * @param {number} duration Délka přehrávání. [s]
   * @private
   * @returns {undefined} Nic.
   */
  onDurationchange(duration) {
    const requestedDuration = this.scheduleInfo.getPlayDuration();
    if (requestedDuration === 0) {
      // Pokud je požadovaná doba přehrávání nulová, použije se výchozí doba přehrávání.
      return;
    }
    // Dle skutečné a požadované doby přehrávání se určí rychlost.
    const playbackRate = (duration * 1000) / requestedDuration;
    return this.parent.setPlaybackRate(playbackRate);
  }

  /**
   * Reakce na chybu v elementu.
   * @private
   * @returns {undefined} Nic.
   */
  onElementError() {
    const error = new Error('Unknown HTMLIframeElement error.');
    this.emitter.emit('error', error);
  }

  /**
   * Dosažení konce přehrávání.
   * @private
   * @returns {undefined} Nic.
   */
  onEnded() {
    this.emitter.emit('played');
  }

  /**
   * Reakce na chybu z PDF přehrávače.
   * @param {Error} error
   * @private
   * @returns {undefined} Nic.
   */
  onParentError(error) {
    this.emitter.emit('error', error);
  }

  /** @inheritdoc */
  release() {
    return this.viewerLifeCycle.release(() => {
      const { viewerSource } = this;
      // Uvolnění obalujícího elementu.
      const frame = viewerSource.getFrame();
      frame.release();
      // Uvolnění zdrojového elementu.
      const el = viewerSource.getEl();
      this.unbindElement(el);
      // Zrušení hodnot v instanci.
      this.scheduleInfo = null;
      this.viewerSource = null;
    });
  }

  /** @inheritdoc */
  seize(scheduleInfo, viewerSource) {
    return this.viewerLifeCycle.seize(() => {
      // Nastavení hodnot v instanci.
      this.scheduleInfo = scheduleInfo;
      this.viewerSource = viewerSource;
      // Určení všech potřebných hodnot.
      return scheduleInfo.getUrl().then((url) => {
        const el = viewerSource.getEl();
        const frame = viewerSource.getFrame();
        // Příprava obalujícího elementu.
        frame.seize(scheduleInfo);
        // Příprava zdrojového elementu.
        this.bindElement(el);
        el.classList.add('pdf-viewer-controller');
        el.sandbox = 'allow-forms allow-same-origin allow-scripts';
        // Deaktivace scrollování v iframe.
        // Označeno jako deprecated, ale některé prohlížeče ho vyžadují.
        el.scrolling = 'no';
        // Nastavení zdroje.
        const parent = viewerSource.getParent();
        this.parent = parent;
        this.bindParent(parent);
        return parent.setSrc(url);
      });
    });
  }

  /**
   * Uvolní naslouchání událostem z elementu.
   * @param {HTMLIFrameElement} el Element, kterému se naslouchá.
   * @private
   * @returns {undefined} Nic.
   */
  unbindElement(el) {
    el.removeEventListener('error', this.onElementError);
  }

  /**
   * Uvolní naslouchání událostem z PDF serveru.
   * @param {PdfParent} parent
   * @private
   * @returns {undefined}
   */
  unbindParent(parent) {
    parent.off('durationchange', this.onDurationchange);
    parent.off('ended', this.onEnded);
    parent.off('error', this.onParentError);
  }
}

export default PdfViewerController;
